<template>
  <div class="flex min-w-0 w-full flex-col gap-4">
    <TypeSizeSearch
      v-if="props.isTyres"
      :code="props.code"
      :initial-diameter="props.initialDiameter"
      :initial-sizes="props.initialSizes"
      :mobile="props.mobile"
      @search="onTypeSizeSearch"
    />

    <div class="flex flex-col gap-3">
      <div
        class="relative w-full"
        :class="props.mobile ? 'h-[56px]' : 'h-[61px] max-w-[373px]'"
      >
        <img
          src="/search.svg"
          class="pointer-events-none absolute top-1/2 h-5 w-5 -translate-y-1/2"
          :class="props.mobile ? 'left-4' : 'left-5'"
        />
        <input
          v-model="filterSearch"
          type="text"
          placeholder="Поиск по фильтрам"
          :class="
            props.mobile
              ? 'h-full w-full rounded-[18px] border border-gray-border bg-white-gray pl-[52px] pr-[52px] text-[16px] font-helvetica-400 text-gray-dark placeholder:text-extra-text-color'
              : 'h-full w-full rounded-[20px] bg-white-gray pl-[60px] pr-[48px] text-[18px] font-helvetica-400 text-extra-text-color'
          "
        />
        <button
          v-if="filterSearch"
          type="button"
          aria-label="Очистить поиск по фильтрам"
          :class="
            props.mobile
              ? 'absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-light transition-colors hover:bg-white hover:text-gray-dark'
              : 'absolute right-5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-light transition-colors hover:text-gray-dark'
          "
          @click="filterSearch = ''"
        >
          <img src="/cros.svg" class="h-4 w-4" />
        </button>
      </div>

      <FilterContainer
        v-if="isLoading || filterEntries.length"
        :class="
          props.mobile ? 'border border-gray-border bg-white p-0' : 'pt-5'
        "
      >
        <template v-if="isLoading">
          <div class="px-5 pt-4 text-[14px] text-extra-text-color">
            Загрузка фильтров...
          </div>
          <div
            v-for="i in 3"
            :key="i"
            class="flex animate-pulse flex-col gap-4 pb-6 pt-4 first:pt-0"
          >
            <div class="flex items-center gap-3 pl-5">
              <div class="h-4 w-4 rounded bg-gray-light/60" />
              <div class="h-5 w-40 rounded bg-gray-light/70" />
            </div>
            <div class="space-y-2 px-5">
              <div class="h-4 w-full rounded bg-gray-light/40" />
              <div class="h-4 w-5/6 rounded bg-gray-light/30" />
              <div class="h-4 w-4/6 rounded bg-gray-light/20" />
            </div>
          </div>
        </template>

        <template v-else>
          <Filter
            v-for="filter in filterEntries"
            :key="filter.requestName"
            :values="filter.values"
            :mobile="props.mobile"
            :open="!!filterSearch"
            :search="filterSearch"
            @update:values="onUpdateValues(filter.requestName, $event)"
          >
            {{ filter.displayName }}
          </Filter>
        </template>
      </FilterContainer>

      <p
        v-else-if="!isLoading"
        class="rounded-[18px] border border-dashed border-gray-border bg-white px-4 py-4 text-[14px] leading-[20px] text-extra-text-color"
      >
        Для этой категории пока нет дополнительных фильтров. Можно искать по
        типоразмеру или сразу показать всю выдачу.
      </p>

      <div
        :class="
          props.mobile
            ? 'sticky bottom-0 z-10 mt-2 border-t border-gray-border bg-white px-1 pb-[calc(20px+env(safe-area-inset-bottom))] pt-4 shadow-[0_-10px_24px_rgb(0_0_0_/_0.05)]'
            : 'pt-1'
        "
      >
        <div :class="props.mobile ? 'flex flex-col gap-2' : 'flex flex-col'">
          <button
            :class="
              props.mobile
                ? 'h-[52px] w-full rounded-[14px] bg-orange text-[16px] font-helvetica-700 text-white transition-colors hover:bg-selected-orange cursor-pointer'
                : 'ml-5 h-[55px] w-[calc(100%-40px)] max-w-[333px] rounded-[10px] bg-orange text-white hover:bg-selected-orange cursor-pointer'
            "
            @click="applyFilters"
          >
            Применить
          </button>
          <button
            :class="
              props.mobile
                ? 'h-[52px] w-full rounded-[14px] border border-gray-dark bg-white text-[16px] font-helvetica-400 text-gray-dark transition-colors hover:bg-white-gray cursor-pointer'
                : 'mb-6.5 ml-5 mt-2.5 h-[55px] w-[calc(100%-40px)] max-w-[333px] rounded-[10px] border border-gray-dark text-gray-dark hover:bg-white cursor-pointer'
            "
            @click="resetAll"
          >
            Сбросить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Filter from "~/components/goods/Filter.vue";
import TypeSizeSearch from "~/components/goods/TypeSizeSearch.vue";
import FilterContainer from "~/components/goods/FilterContainer.vue";
import { useFacetedFiltersApi } from "~/composables/useFacetedFiltersApi";
import type { FilterValues } from "~/types/filterValues";
import {
  getFacetedFilterDefinitions,
  type FacetedFilterDefinition,
} from "~/utils/facetedFilterDefinitions";

const facetedFiltersApi = useFacetedFiltersApi();

const props = withDefaults(
  defineProps<{
    code: number;
    initialFilters?: string;
    initialDiameter?: string;
    initialSizes?: string[];
    isTyres?: boolean;
    mobile?: boolean;
  }>(),
  {
    mobile: false,
  },
);

const emit = defineEmits<{
  (
    e: "apply",
    payload: {
      filters: string;
      sizes: string[];
      diameter: string;
    },
  ): void;
}>();

type FilterGroup = {
  requestName: string;
  displayName: string;
  needName: string;
  values: FilterValues[];
};

const filters = ref<FilterGroup[]>([]);
const filterSearch = ref("");
const isLoading = ref(true);
const facetRequestId = ref(0);
const selectedByRequestName = ref<Record<string, string[]>>({});
const typeSizeParams = ref<{ sizes: string[]; diameter: string } | null>(null);
const useTypeSize = ref(false);

const filterEntries = computed(() => {
  const q = filterSearch.value.trim().toLowerCase();
  const visibleFilters = filters.value.filter(
    (filter) =>
      filter.values.length ||
      (selectedByRequestName.value[filter.requestName]?.length ?? 0) > 0,
  );

  if (!q) {
    return visibleFilters;
  }

  return visibleFilters.filter(
    (filter) =>
      filter.displayName.toLowerCase().includes(q) ||
      filter.needName.toLowerCase().includes(q) ||
      filter.requestName.toLowerCase().includes(q) ||
      filter.values.some((v) => v.label.toLowerCase().includes(q)),
  );
});

const buildFiltersMap = (definitions: FacetedFilterDefinition[]) =>
  definitions.map((definition) => ({
    ...definition,
    values: [] as FilterValues[],
  }));

const getSelectedFacetedFilters = () => {
  return filters.value.reduce<Record<string, string[]>>((acc, filter) => {
    const selectedValues =
      selectedByRequestName.value[filter.requestName] ?? [];

    if (selectedValues.length) {
      acc[filter.needName] = selectedValues;
    }

    return acc;
  }, {});
};

const applyFacetedValues = (
  data: Record<string, { value: string; count: number }[]>,
) => {
  filters.value = filters.value.map((filter) => {
    const selectedValues = new Set(
      selectedByRequestName.value[filter.requestName] ?? [],
    );
    const responseValues = Array.isArray(data[filter.needName])
      ? data[filter.needName]
      : [];
    const nextValues = responseValues
      .map((item) => {
        const label = String(item?.value ?? "");
        if (!label) return null;

        return {
          value: label,
          label,
          count:
            typeof item?.count === "number"
              ? item.count
              : Number(item?.count ?? 0),
          selected: selectedValues.has(label),
        };
      })
      .filter((value): value is FilterValues => Boolean(value));
    const returnedLabels = new Set(nextValues.map((value) => value.label));

    selectedValues.forEach((label) => {
      if (!returnedLabels.has(label)) {
        nextValues.push({
          value: label,
          label,
          count: 0,
          selected: true,
        });
      }
    });

    return {
      ...filter,
      values: nextValues,
    };
  });
};

const refreshFacetedValues = async () => {
  if (!facetedFiltersApi.isConfigured || !filters.value.length) return;

  const requestId = facetRequestId.value + 1;
  facetRequestId.value = requestId;

  try {
    const data = await facetedFiltersApi.getFilters({
      groupId: props.code,
      need: filters.value.map((filter) => filter.needName),
      selected: getSelectedFacetedFilters(),
    });

    if (requestId !== facetRequestId.value) return;

    applyFacetedValues(data);
  } catch (error) {
    console.error("Error fetching faceted filters", error);
  }
};

const syncFiltersFromString = (filtersString: string) => {
  if (!filters.value.length) return;

  const parsed = filtersString
    .split("|")
    .reduce<Record<string, Set<string>>>((acc, part) => {
      const [rawName, rawValues] = part.split("=");
      if (!rawName || !rawValues) return acc;
      const name = rawName.trim();
      const values = rawValues
        .split(";")
        .map((v) => v.trim())
        .filter(Boolean);
      acc[name] = new Set(values);
      return acc;
    }, {});

  selectedByRequestName.value = Object.fromEntries(
    Object.entries(parsed).map(([name, values]) => [name, Array.from(values)]),
  );

  filters.value = filters.value.map((filter) => {
    const selectedValues = selectedByRequestName.value[filter.requestName];
    return {
      ...filter,
      values: filter.values.map((value) => ({
        ...value,
        selected: selectedValues ? selectedValues.has(value.label) : false,
      })),
    };
  });
};

const loadFilters = async () => {
  isLoading.value = true;
  filters.value = [];

  try {
    filters.value = buildFiltersMap(getFacetedFilterDefinitions(props.code));
    syncFiltersFromString(props.initialFilters ?? "");
    await refreshFacetedValues();
  } catch (e) {
    console.error("Error fetching filters", e);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.code,
  () => {
    loadFilters();
  },
  { immediate: true },
);

watch(
  () => props.initialFilters ?? "",
  async (filtersString) => {
    if (isLoading.value) return;
    syncFiltersFromString(filtersString);
    await refreshFacetedValues();
  },
  { immediate: true },
);

watch(
  () => [props.initialDiameter ?? "", (props.initialSizes ?? []).join(";")],
  ([diameter, sizes]) => {
    const normalizedSizes = sizes ? sizes.split(";").filter(Boolean) : [];
    const hasTypeSize = Boolean(diameter) || normalizedSizes.length > 0;

    useTypeSize.value = hasTypeSize;
    typeSizeParams.value = hasTypeSize
      ? {
          diameter,
          sizes: normalizedSizes,
        }
      : null;
  },
  { immediate: true },
);

const onUpdateValues = (requestName: string, updated: FilterValues[]) => {
  filters.value = filters.value.map((filter) =>
    filter.requestName === requestName
      ? { ...filter, values: updated }
      : filter,
  );
  selectedByRequestName.value = {
    ...selectedByRequestName.value,
    [requestName]: updated
      .filter((value) => value.selected && value.value !== "all")
      .map((value) => value.label),
  };
  void refreshFacetedValues();
};

const buildFiltersString = () => {
  const parts: string[] = [];

  filters.value.forEach((filter) => {
    const selected = selectedByRequestName.value[filter.requestName] ?? [];
    if (!selected.length) return;

    const valueString = selected.join(";");
    parts.push(`${filter.requestName}=${valueString}`);
  });

  return parts.join("|");
};

const applyFilters = () => {
  useTypeSize.value = false;
  typeSizeParams.value = null;

  emit("apply", {
    filters: buildFiltersString(),
    sizes: [],
    diameter: "",
  });
};

const onTypeSizeSearch = (payload: { sizes: string[]; diameter: string }) => {
  const sizes = Array.isArray(payload?.sizes)
    ? payload.sizes.filter(Boolean)
    : [];
  const diameter = payload?.diameter || "";

  typeSizeParams.value = { sizes, diameter };
  useTypeSize.value = Boolean(sizes.length || diameter);

  emit("apply", {
    filters: "",
    sizes,
    diameter,
  });
};

const resetAll = () => {
  filterSearch.value = "";
  typeSizeParams.value = null;
  useTypeSize.value = false;
  selectedByRequestName.value = {};

  filters.value = filters.value.map((filter) => ({
    ...filter,
    values: filter.values.map((value) => ({
      ...value,
      selected: false,
    })),
  }));
  void refreshFacetedValues();
  emit("apply", {
    filters: "",
    sizes: [],
    diameter: "",
  });
};
</script>
